import { db } from 'firebase/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

import { getCollectionData } from '@src/packages/utils';
import {
  chunkIds,
  FIRESTORE_IN_LIMIT,
} from '@src/packages/users/screens/UsersList/utils/buildUserQueries';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { createOperation } from '@src/util/firebase-operations/utils/createOperation';
import safeExecute from '@src/util/safeExecute';

import elasticQueryClient from './ElasticQueryClient';

import { FSCollections } from '../constants/database';

class WorkJourneyRepository {
  constructor() {
    this.workJourneys = [];
    this.workJourneysCollection = FSCollections.WORK_JOURNEYS;
    this.workJourneyConfigsCollection = FSCollections.WORK_JOURNEY_CONFIGS;
    this.organizationCollection = FSCollections.ORGANIZATIONS;
  }

  getWorkJourneysPath(orgId) {
    return `${this.organizationCollection}/${orgId}/${this.workJourneysCollection}`;
  }

  getWorkJourneyConfigsPath(orgId) {
    return `${this.organizationCollection}/${orgId}/${this.workJourneyConfigsCollection}`;
  }

  getWorkJourneyMembersPath(orgId, workJourneyId) {
    return `${this.organizationCollection}/${orgId}/${this.workJourneyConfigsCollection}/${workJourneyId}/${FSCollections.WORK_JOURNEY_MEMBERS}`;
  }

  getWorkJourneySupervisorsPath(orgId, workJourneyId) {
    return `${this.organizationCollection}/${orgId}/${this.workJourneyConfigsCollection}/${workJourneyId}/${FSCollections.WORK_JOURNEY_SUPERVISORS}`;
  }

  // Create
  async create(workJourneyDataConfig, organizationId) {
    // Extract members and supervisors before modifying the object
    const { members, supervisors } = workJourneyDataConfig;

    const configResponse = await safeExecute(async () => {
      // Create a copy of the object without members and supervisors
      const cleanData = { ...workJourneyDataConfig };
      delete cleanData.members;
      delete cleanData.supervisors;

      const res = await createOperation(
        this.getWorkJourneyConfigsPath(organizationId),
        null,
        cleanData,
        'create',
        false,
        true,
        true,
      );
      return res;
    });

    // Create work journey members and supervisors
    const workJourneyId = configResponse.id;
    const membersPromises = [];
    const supervisorsPromises = [];

    if (members && members.length > 0) {
      const memberPath = this.getWorkJourneyMembersPath(organizationId, workJourneyId);
      members.forEach(member => {
        const memberData = { ...member };

        delete memberData.divisions;
        const memberId = memberData.user?.id || memberData.id;
        if (!memberId) throw new Error('Work journey member id is required');
        const memberPromise = createOperation(memberPath, memberId, memberData, 'create');
        membersPromises.push(memberPromise);
      });
    }

    if (supervisors && supervisors.length > 0) {
      const supervisorsPath = this.getWorkJourneySupervisorsPath(organizationId, workJourneyId);
      supervisors.forEach(supervisor => {
        const supervisorData = { ...supervisor };

        delete supervisorData.divisions;
        const supervisorId = supervisorData.user?.id || supervisorData.id;
        if (!supervisorId) throw new Error('Work journey supervisor id is required');
        const supervisorPromise = createOperation(
          supervisorsPath,
          supervisorId,
          supervisorData,
          'create',
        );
        supervisorsPromises.push(supervisorPromise);
      });
    }

    const [membersResults, supervisorsResults] = await Promise.all([
      Promise.all(membersPromises),
      Promise.all(supervisorsPromises),
    ]);

    return {
      configResponse,
      members: membersResults,
      supervisors: supervisorsResults,
    };
  }

  // List
  async listConfigs(organizationId) {
    return safeExecute(async () => {
      if (!organizationId) return [];

      const configsRef = collection(
        db,
        this.organizationCollection,
        organizationId,
        this.workJourneyConfigsCollection,
      );
      const querySnapshot = await getDocs(configsRef);
      return getCollectionData(querySnapshot);
    });
  }

  // Update
  async update(workJourneyData, organizationId) {
    return safeExecute(async () => {
      const updateWorkJourneyFn = apiCallable(API_OPERATIONS.UPDATE_WORK_JOURNEY);
      const response = await updateWorkJourneyFn({ workJourneyData, organizationId });
      return response.data;
    });
  }

  // Delete
  async delete(workJourneyId, organizationId) {
    return safeExecute(async () => {
      const url = this.getWorkJourneyConfigsPath(organizationId);
      const res = await createOperation(url, workJourneyId, null, 'delete');
      return res;
    });
  }

  async findWorkJourneyById(organizationId, workJourneyId) {
    return safeExecute(async () => {
      if (!organizationId || !workJourneyId) {
        return null;
      }

      const workJourneyRef = doc(
        db,
        this.organizationCollection,
        organizationId,
        this.workJourneysCollection,
        workJourneyId,
      );
      const workJourneySnap = await getDoc(workJourneyRef);

      if (!workJourneySnap.exists()) {
        return null;
      }

      return { id: workJourneySnap.id, ...workJourneySnap.data() };
    });
  }

  async getMembers(organizationId, workJourneyId) {
    return safeExecute(
      async () => {
        if (!organizationId || !workJourneyId) {
          return [];
        }

        const snapshot = await getDocs(
          collection(
            db,
            this.organizationCollection,
            organizationId,
            this.workJourneyConfigsCollection,
            workJourneyId,
            FSCollections.WORK_JOURNEY_MEMBERS,
          ),
        );

        return getCollectionData(snapshot);
      },
      'WorkJourneyRepository.getMembers',
      { organizationId, workJourneyId },
    );
  }

  async updateMembers(workJourneyId, organizationId, members) {
    return safeExecute(async () => {
      const updateWorkJourneyFn = apiCallable(API_OPERATIONS.UPDATE_WORK_JOURNEY_MEMBERS);
      const response = await updateWorkJourneyFn({ workJourneyId, organizationId, members });
      return response.data;
    });
  }

  queryElastic(params) {
    return elasticQueryClient.query(params);
  }

  /**
   * Resolves work journey config ids that include any of the given users as members.
   * Uses per-config member subcollection queries (collection group is blocked by rules).
   */
  listConfigIdsByMemberUserIds = async (organizationId, userIds) =>
    safeExecute(
      async () => {
        if (!organizationId || !userIds?.length) {
          return [];
        }

        const uniqueUserIds = [...new Set(userIds.filter(Boolean))];
        if (!uniqueUserIds.length) {
          return [];
        }

        const configsSnapshot = await getDocs(
          collection(
            db,
            this.organizationCollection,
            organizationId,
            this.workJourneyConfigsCollection,
          ),
        );
        const configIds = configsSnapshot.docs.map(configDoc => configDoc.id);
        if (!configIds.length) {
          return [];
        }

        const matchingConfigIds = new Set();

        for (const userIdChunk of chunkIds(uniqueUserIds, FIRESTORE_IN_LIMIT)) {
          await Promise.all(
            configIds.map(async configId => {
              const membersQuery = query(
                collection(
                  db,
                  this.organizationCollection,
                  organizationId,
                  this.workJourneyConfigsCollection,
                  configId,
                  FSCollections.WORK_JOURNEY_MEMBERS,
                ),
                where('user.id', 'in', userIdChunk),
              );
              const snapshot = await getDocs(membersQuery);

              if (!snapshot.empty) {
                matchingConfigIds.add(configId);
              }
            }),
          );
        }

        return [...matchingConfigIds];
      },
      'WorkJourneyRepository.listConfigIdsByMemberUserIds',
      { organizationId, userCount: userIds.length },
    );
}

export default WorkJourneyRepository;
